import pLimit from 'p-limit';
import { Fill, Stroke, Style, Icon, Circle as CircleStyle } from 'ol/style';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { fromLonLat } from 'ol/proj';
import arrowIcon from '@assets/arrow.svg';

import { fetchUrl } from '@utils/api/files';

const limit = pLimit(5); // Limit to 5 concurrent requests

const constructUrl = (projectUuid, path, imageName) => {
	return `filelink?key=${projectUuid}/${path}/${imageName}`;
};

/**
 * Fetches image data for a list of grid features.
 * Only fetches thumbnails upfront. Compressed and full-res URLs are fetched lazily on demand.
 * @param {Array} gridFeatures - The list of grid features.
 * @param {string} projectUuid - The UUID of the project.
 * @param {AbortSignal} signal - The signal to abort the fetch requests.
 * @returns {Promise<Array>} - A promise that resolves to an array of grid features with image data.
 */
export const fetchFeaturesImageData = async ({
	gridFeatures,
	projectUuid,
	signal,
	updateLoadedImages,
	updateImagesNotReady,
}) => {
	let notLoadedImages = 0;

	// Map over the grid features and fetch thumbnail data for each feature
	const promises = [];

	for (const feature of gridFeatures) {
		promises.push(
			limit(async () => {
				// Extract the image name from the feature properties
				const imageName = feature.properties.tile_name;

				// Construct the URL for the thumbnail image
				const thumbnailUrl = constructUrl(projectUuid, 'images/thumbnails', imageName);

				let thumbnail;
				try {
					// Fetch the thumbnail image
					thumbnail = await fetchUrl(thumbnailUrl, signal);
				} catch (error) {
					console.error('Error fetching thumbnail image:', error);
				}

				if (!thumbnail?.url) {
					notLoadedImages++;

					// If many images are not reachable, the project is propably not ready yet
					if (notLoadedImages >= 10) {
						limit.clearQueue();
						updateImagesNotReady();
					}

					return null;
				}

				updateLoadedImages();

				// Return the feature with only the thumbnail — compressed/full-res fetched on demand
				return {
					...feature,
					thumbnail: thumbnail.url,
				};
			})
		);
	}

	// Wait for all promises to resolve and return the results
	return Promise.all(promises);
};

/**
 * Fetches compressed and full resolution image URLs for a single image on demand.
 * @param {string} projectUuid - The UUID of the project.
 * @param {string} imageName - The image file name.
 * @param {AbortSignal} signal - The signal to abort the fetch requests.
 * @returns {Promise<{compressedUrl: string|null, fullResUrl: string|null}>}
 */
export const fetchImageUrls = async (projectUuid, imageName, signal) => {
	const compressedUrl = constructUrl(projectUuid, 'images/compressed', imageName);
	const fullUrl = constructUrl(projectUuid, 'images', imageName);

	const [compressedImage, fullImage] = await Promise.all([
		fetchImage(compressedUrl, signal),
		fetchImage(fullUrl, signal),
	]);

	return {
		compressedUrl: compressedImage?.url ?? null,
		fullResUrl: fullImage?.url ?? null,
	};
};

/**
 * Fetches an image from a URL.
 * @param {string} url - The URL of the image.
 * @param {AbortSignal} signal - The signal to abort the fetch request.
 * @returns {Promise<Object|null>} - The fetched image object or null if fetch fails.
 */
const fetchImage = async (url, signal) => {
	try {
		// Attempt to fetch the image
		return await fetchUrl(url, signal);
	} catch (error) {
		// Log an error if fetching the image fails
		console.error('Error fetching image:', error);

		// Return null if the fetch attempt fails
		return null;
	}
};

/**
 * Generates styles for map features based on their properties and the map's zoom level.
 * @param {Object} mapObject - The map object.
 * @returns {Function} - A function that takes a feature and returns an array of styles.
 */
export const styleFunction = ({
	feature,
	mapObject,

	// Defaults
	fillColor,
	radius = 8,
	strokeColor = [0, 0, 0, 1], // Black
	strokeWidth = 1,
}) => {
	// Determine if the feature has detections
	const isDetect = !!feature.get('detections');

	if (!fillColor) fillColor = isDetect ? [0, 128, 0, 1] : [255, 255, 0, 1]; // green : yellow in RGBA

	if (!feature.get('hasInitialStyle')) {
		feature.set('hasInitialStyle', true);
		feature.set('fillColor', fillColor);
		feature.set('strokeColor', strokeColor);
		feature.set('radius', radius);
		feature.set('strokeWidth', strokeWidth);
	}

	// If the feature has annotations, add a highlight ring beneath the main circle
	const hasAnnotations = !!feature.get('annotations');
	const styles = [];

	if (hasAnnotations) {
		styles.push(
			new Style({
				image: new CircleStyle({
					radius: radius + 4,
					fill: new Fill({
						color: 'rgba(0, 0, 0, 0)', // Transparent fill — ring only
					}),
					stroke: new Stroke({
						color: 'rgba(255, 165, 0, 1)', // Orange highlight ring for annotated points
						width: 3,
						lineDash: [4, 3], // Dashed to match training data pattern
					}),
				}),
				zIndex: 13, // Below the main circle (zIndex 14)
			})
		);
	}

	// Base style with a circle, green if detections exist, yellow otherwise
	styles.push(
		new Style({
			image: new CircleStyle({
				radius: radius,
				fill: new Fill({
					color: `rgba(${fillColor})`,
				}),
				stroke: new Stroke({
					color: `rgba(${strokeColor})`,
					width: strokeWidth,
				}),
			}),
			zIndex: 14,
		})
	);

	// Get the current zoom level of the map
	const view = mapObject.getView();
	const zoom = view.getZoom();
	const minZoom = 18; // Minimum zoom level to display the arrow icon

	// Add an arrow icon if the zoom level is greater than the minimum zoom level
	if (zoom > minZoom) {
		const rotation = feature.get('rotation');

		styles.push(
			new Style({
				image: new Icon({
					src: arrowIcon,
					scale: 1.2,
					anchor: [0.5, 1],
					anchorXUnits: 'fraction',
					anchorYUnits: 'fraction',
					rotation: rotation * (Math.PI / 180), // Convert rotation to radians
				}),
				zIndex: 15,
			})
		);
	}

	// Return the array of styles
	return styles;
};

/**
 * Creates a new feature for the single image layer.
 *
 * @param {Object} data - The data for the feature.
 * @returns {Feature} - The created feature.
 */
export const createFeature = data => {
	try {
		// Destructure the data object to extract necessary properties
		const { image, fullResImage, thumbnail, properties, geometry } = data;
		const { tile_name: imageName, drone_location, image_location, id } = properties;
		const { longitude, latitude, gimbal_orientation } = drone_location ?? image_location;
		const { coordinates } = geometry;

		// Create a new feature with the provided data
		return new Feature({
			geometry: new Point(
				// Convert the longitude and latitude to the default projection
				[longitude, latitude]
			),
			image: image ?? null,
			fullResImage: fullResImage ?? null,
			thumbnail: thumbnail,
			polygons: coordinates[0],
			name: imageName,
			rotation: gimbal_orientation?.yaw,
			uuid: id,
			center: [latitude, longitude],
			featureType: 'imagePoint',
		});
	} catch (error) {
		console.warn('Error creating single image feature:', error);
		return null;
	}
};

// Helper function to interpolate values
const interpolate = (start, end, t) => {
	if (typeof start != 'object' && typeof end != 'object') return start + (end - start) * t;

	start = Object.values(start);
	end = Object.values(end);

	const arrLength = start.length;
	const interpolatedArray = [];
	for (let i = 0; i < arrLength; i++) {
		const value = start[i] + (end[i] - start[i]) * t;
		if (i != arrLength - 1) interpolatedArray.push(Math.round(value));
		else interpolatedArray.push(value);
	}

	return interpolatedArray;
};

const reverseValues = ([initial, target], reverse = false) =>
	reverse ? [target, initial] : [initial, target];

// Function to animate feature properties
export const animateFeature = ({
	mapObject,
	feature,
	duration = 150,
	makeActive = true,
	onComplete,
}) => {
	const start = performance.now();
	const radius = feature.get('radius');
	const fillColor = feature.get('fillColor');
	const strokeColor = feature.get('strokeColor');
	const strokeWidth = feature.get('strokeWidth');

	const hoverRadius = 12;
	const hoverFillColor = [0, 128, 0, 0.8];
	const hoverStroke = [255, 255, 0, 1];
	const hoverStrokeWidth = 3;

	const properties = Object.entries({
		radius: [radius, hoverRadius],
		fillColor: [fillColor, hoverFillColor],
		strokeColor: [strokeColor, hoverStroke],
		strokeWidth: [strokeWidth, hoverStrokeWidth],
	});

	const step = timestamp => {
		const elapsed = timestamp - start;
		const t = Math.max(0, Math.min(elapsed / duration, 1));

		// handles animation direction and interpolation for all properties
		const styleProperties = properties.reduce((acc, [key, [start, end]]) => {
			[start, end] = reverseValues([start, end], !makeActive);
			acc[key] = interpolate(start, end, t);
			return acc;
		}, {});

		const style = styleFunction({
			mapObject,
			feature,
			...styleProperties,
		});

		feature.setStyle(style);

		if (t < 1) requestAnimationFrame(step);
		else if (onComplete) onComplete();
	};

	requestAnimationFrame(step);
};

export const getImagePoint = (layer, filename) =>
	layer
		?.getSource()
		?.getFeatures()
		?.find(feature => feature.get('name') === filename);


/**
 * Matches GeoJSON features to OpenLayers map layer features by tile_name → feature name.
 * Groups the GeoJSON features, aggregates class info, and sets the result on each map feature.
 *
 * @param {Object} params
 * @param {import('ol/layer/Vector').default} params.layer - The OL VectorLayer containing image point features
 * @param {Object|null} params.geojsonData - GeoJSON FeatureCollection with `features` array (or null/undefined)
 * @param {string} params.propertyKey - The feature property key to set (e.g. 'detections' or 'annotations')
 * @returns {{ features: Array, uniqueClasses: Array<{id: any, label: string}> }}
 *   - features: the OL features array from the layer
 *   - uniqueClasses: deduplicated array of { id, label } across all matched GeoJSON features
 */
export const matchGeoJsonToFeatures = ({ layer, geojsonData, propertyKey }) => {
	const features = layer?.getSource()?.getFeatures() ?? [];

	// Clear existing property from all features
	features.forEach(feature => {
		feature.set(propertyKey, null);
	});

	if (!geojsonData?.features?.length) {
		return { features, uniqueClasses: [] };
	}

	// Group by tile_name for O(n) lookup + collect unique classes
	const tileNameMap = new Map();
	const uniqueClassesMap = new Map();

	geojsonData.features.forEach(item => {
		const tileName = item.properties.tile_name;

		if (!tileNameMap.has(tileName)) {
			tileNameMap.set(tileName, []);
		}
		tileNameMap.get(tileName).push(item);

		// Track unique classes
		const { classid: id, classname: label } = item.properties;
		const key = `${id}-${label}`;
		if (!uniqueClassesMap.has(key)) {
			uniqueClassesMap.set(key, { id, label });
		}
	});

	// Match to map features by name
	features.forEach(feature => {
		const filename = feature.get('name');
		const matching = tileNameMap.get(filename);

		if (matching?.length > 0) {
			const classes = matching.reduce((acc, item) => {
				const { classname: name, classid: id } = item.properties;
				const existing = acc.find(c => c.name === name && c.id === id);
				if (existing) existing.count += 1;
				else acc.push({ name, id, count: 1 });
				return acc;
			}, []);

			feature.set(propertyKey, { features: matching, classes });
		}
	});

	return {
		features,
		uniqueClasses: Array.from(uniqueClassesMap.values()),
	};
};

/**
 * Clears a property from all features on a layer.
 *
 * @param {import('ol/layer/Vector').default} layer - The OL VectorLayer
 * @param {string} propertyKey - The property to clear (set to null)
 */
export const clearFeatureProperty = (layer, propertyKey) => {
	if (!layer) return;

	const features = layer.getSource()?.getFeatures() ?? [];
	features.forEach(feature => {
		feature.set(propertyKey, null);
	});
};
